import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/react";
import { Button } from "@heroui/react";
import { Pause, Play, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useWakeLock } from "react-screen-wake-lock";
import { SETTINGS } from "../../utils/settings";
import { getCurrentDateTime, isMeetingEnded } from "@/utils/dates";
import { getByShortCode as getByShortCodeApi, setMeetsStartTime as setMeetsStartTimeApi, updateMeet as updateMeetApi } from "@/api/meets";
import { metrics, startSession, recordSectionStart, recordSectionEnd, summarize } from "@/utils/metrics";
import CreateMemberDrawer from "../drawers/create-member-drawer";
import AddMembersDrawer from "../drawers/add-members-drawer";
import { toConnectionShape, toMeetingMemberShape } from "@/utils/meets";

const API_URL = `${SETTINGS["API_ENDPOINT"]}/audio/api`;
const SECTION_MS = 10_000;

const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const extFromMime = (mime) => {
  if (!mime) return ".webm";
  const base = mime.split(";")[0].toLowerCase();
  if (base.includes("wav")) return ".wav";
  if (base.includes("mp4")) return ".m4a";
  if (base.includes("ogg")) return ".ogg";
  if (base.includes("webm")) return ".webm";
  return ".webm";
};

function floatTo16BitPCM(float32Array) {
  const output = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function encodeWAV(float32Data, sampleRate) {
  const channelCount = 1;
  const pcm16 = floatTo16BitPCM(float32Data);
  const bytesPerSample = 2;
  const blockAlign = channelCount * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcm16.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  writeString(view, 22, "\x01\x00");
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);
  let offset = 44;
  for (let i = 0; i < pcm16.length; i++, offset += 2) {
    view.setInt16(offset, pcm16[i], true);
  }
  return new Blob([view], { type: "audio/wav" });
}

function resampleLinear(float32, fromRate, toRate) {
  if (fromRate === toRate) return float32;
  const ratio = toRate / fromRate;
  const newLen = Math.max(1, Math.round(float32.length * ratio));
  const out = new Float32Array(newLen);
  const step = (float32.length - 1) / (newLen - 1);
  for (let i = 0; i < newLen; i++) {
    const pos = i * step;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, float32.length - 1);
    const t = pos - i0;
    out[i] = float32[i0] * (1 - t) + float32[i1] * t;
  }
  return out;
}

const SkeletonBar = ({ className = "h-4 w-24" }) => (
  <span className={`inline-block rounded-full bg-default/30 dark:bg-white/20 animate-pulse align-middle ${className}`} />
);

function getMeetingStartTimestamp(record) {
  const real = record?.start_time?.real;
  const dateStr = record?.meeting_date;
  if (!real || !dateStr) return null;
  const ts = new Date(`${dateStr}T${real}:00`).getTime();
  return Number.isNaN(ts) ? null : ts;
}

const ActiveMeetsComp = ({ _short_code }) => {
  const { session } = useSession();
  const navigate = useNavigate();

  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [status, setStatus] = useState("Idle");
  const [recordedTime, setRecordedTime] = useState(0);
  const [stitchingAudio, setStitchingAudio] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [meetingStarted, setMeetingStarted] = useState(false);
  const [showAddMemeber, setShowAddMember] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const pendingUploadsRef = useRef(new Set());
  const failedUploadsRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const micStreamRef = useRef(null);
  const sectionIndexRef = useRef(0);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const procRef = useRef(null);
  const wavSampleRateRef = useRef(0);
  const wavBufferRef = useRef(new Float32Array(0));
  const wavSamplesPerSectionRef = useRef(0);
  const safariPausedRef = useRef(false);
  const hasSetStartTimeRef = useRef(false);
  const autoStartedRef = useRef(false);
 
  const meetingStartTsRef = useRef(null);

  const { isSupported, request, release } = useWakeLock();

  useEffect(() => {
    getMeetingRec();
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Your browser does not support audio recording");
    }
    return () => {
      try {
        clearInterval(timerRef.current);
        stopSafariWavIfRunning();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
      } catch {}
      releaseWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && recording) {
        setTimeout(acquireWakeLock, 50);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [recording, isSupported]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (recording) {
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [recording]);

  useEffect(() => {
    if (meeting && !meeting.completed && meeting.created_by?.user_id === session?.user?.id && !isMeetingEnded(meeting) && !autoStartedRef.current) {
      autoStartedRef.current = true;
      startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting]);

  async function getMeetingRec() {
    try {
      const token = await session.getToken();
      const _resp = await getByShortCodeApi(_short_code, token);
      if (_resp.data.Result === "OK") {
        const record = _resp.data.Record;
        const real_end_time = record?.end_time?.real || record?.end_time?.set;
        if (record.completed || record.created_by?.user_id !== session?.user?.id || isMeetingEnded(record)) return navigate(`/meetings/view/${_short_code}`);
        setMeeting(record);

        const isStarted = record?.start_time?.real && record.start_time.real !== "";
        setMeetingStarted(Boolean(isStarted));
        hasSetStartTimeRef.current = Boolean(isStarted);

        if (isStarted) {
          const startTs = getMeetingStartTimestamp(record);
          if (startTs) {
            meetingStartTsRef.current = startTs;
            setRecordedTime(Math.max(0, Math.floor((Date.now() - startTs) / 1000)));
          }
        }
      } else {
        setMeeting(null);
      }
    } catch (e) {
      console.error(e);
      setMeeting(null);
    }
  }

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const pickMimeType = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/ogg", "audio/mp4"];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported?.(type)) return type;
    }
    return "";
  };

  const uploadSection = (blob, sectionIndex) => {
    const upload = sendSection(blob, sectionIndex);
    pendingUploadsRef.current.add(upload);
    upload.finally(() => pendingUploadsRef.current.delete(upload));
    return upload;
  };

  const sendSection = async (blob, sectionIndex) => {
    recordSectionStart(sectionIndex, blob.size);
    const ext = extFromMime(blob.type);
    const filename = `section-${sectionIndex.toString().padStart(6, "0")}${ext}`;
    const typedBlob = new Blob([blob], { type: blob.type || "audio/webm" });
    const current_date_time = getCurrentDateTime();
    const formData = new FormData();
    formData.append("file", typedBlob, filename);
    formData.append("meeting_id", meeting?._id || "");
    formData.append("section_index", sectionIndex);
    formData.append("blob_mime", typedBlob.type || "");
    formData.append("date_time", current_date_time);
    try {
      const token = await session.getToken();
      const res = await axios.post(`${API_URL}/upload-section`, formData, {
        headers: { "Content-Type": "multipart/form-data", TOKEN: token },
      });
      recordSectionEnd(sectionIndex, true, res.status);
    } catch (err) {
      recordSectionEnd(sectionIndex, false, err?.response?.status || "ERR_UPLOAD");
      failedUploadsRef.current = true;
      console.error("Upload section failed:", err);
    }
  };

  const updateMeet = async (record) => {
    setIsUpdating(true);
    const token = await session.getToken();

    updateMeetApi(record, token)
      .then((_resp) => {
        if (_resp.data.Result === "SUCCESS") {
          getMeetingRec();
        } else {
          throw new Error("Failed to update meet");
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  async function onAddMemberDrawerResp(_cmd, _value) {
    if (_cmd === "close") {
      setShowAddMember(false);
      // getMembers();
    } else if (_cmd === "confirm") {
      const payload = {
        ...meeting,
        members: _value.map((m) => m.id),
      };
      await updateMeet(payload);
      setShowAddMember(false);
    }
  }

  const acquireWakeLock = async () => {
    if (!isSupported) return;
    try {
      await request();
    } catch (e) {
      console.warn("WakeLock request failed:", e);
    }
  };

  const releaseWakeLock = async () => {
    if (!isSupported) return;
    try {
      await release();
    } catch (e) {
      console.warn("WakeLock release failed:", e);
    }
  };

  async function getFreshMicStream() {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    });
  }

  async function ensureMicPermission() {
    return await getFreshMicStream();
  }

  const startMediaRecorder = async (providedStream) => {
    const stream = providedStream || (await ensureMicPermission());
    micStreamRef.current = stream;
    const mimeType = pickMimeType();
    const opts = mimeType ? { mimeType, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 };
    const recorder = new MediaRecorder(stream, opts);
    if (!recording && !paused) {
      sectionIndexRef.current = 0;
    }
    recorder.ondataavailable = async (event) => {
      if (event.data?.size > 0) {
        const idx = sectionIndexRef.current++;
        await uploadSection(event.data, idx);
      }
    };
    recorder.onstop = async () => {
      clearInterval(timerRef.current);
      await releaseWakeLock();
      await finalizeSession(micStreamRef.current);
    };
    recorder.start(SECTION_MS);
    mediaRecorderRef.current = recorder;
    startTicking();
    setStatus("Recording");
    setRecording(true);
    setPaused(false);
  };

  const startSafariWav = async (providedStream) => {
    const stream = providedStream || (await ensureMicPermission());
    micStreamRef.current = stream;
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    wavSampleRateRef.current = audioCtx.sampleRate;
    if (!recording && !paused) {
      wavBufferRef.current = new Float32Array(0);
      sectionIndexRef.current = 0;
    }
    wavSamplesPerSectionRef.current = Math.floor((wavSampleRateRef.current * SECTION_MS) / 1000);
    safariPausedRef.current = false;
    processor.onaudioprocess = async (e) => {
      if (safariPausedRef.current) return;
      const input = e.inputBuffer.getChannelData(0);
      const prev = wavBufferRef.current;
      const merged = new Float32Array(prev.length + input.length);
      merged.set(prev, 0);
      merged.set(input, prev.length);
      wavBufferRef.current = merged;
      while (wavBufferRef.current.length >= wavSamplesPerSectionRef.current) {
        const section = wavBufferRef.current.slice(0, wavSamplesPerSectionRef.current);
        wavBufferRef.current = wavBufferRef.current.slice(wavSamplesPerSectionRef.current);
        const TARGET_SR = 16000;
        const section16k = resampleLinear(section, wavSampleRateRef.current, TARGET_SR);
        const wavBlob = encodeWAV(section16k, TARGET_SR);
        const idx = sectionIndexRef.current++;
        await uploadSection(new Blob([wavBlob], { type: "audio/wav" }), idx);
      }
    };
    source.connect(processor);
    processor.connect(audioCtx.destination);
    audioCtxRef.current = audioCtx;
    sourceRef.current = source;
    procRef.current = processor;
    startTicking();
    setStatus("Recording");
    setRecording(true);
    setPaused(false);
  };

  const stopSafariWavIfRunning = () => {
    try {
      if (procRef.current) procRef.current.disconnect();
      if (sourceRef.current) sourceRef.current.disconnect();
      if (audioCtxRef.current) audioCtxRef.current.close();
    } catch {}
    procRef.current = null;
    sourceRef.current = null;
    audioCtxRef.current = null;
  };

  const startTicking = () => {
    clearInterval(timerRef.current);
    const tick = () => {
      if (meetingStartTsRef.current) {
        setRecordedTime(Math.max(0, Math.floor((Date.now() - meetingStartTsRef.current) / 1000)));
      } else {
        setRecordedTime((prev) => prev + 1);
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  };
  const stopTicking = () => clearInterval(timerRef.current);

  const finalizeSession = async (stream) => {
    setStitchingAudio(true);
    try {
      await Promise.all([...pendingUploadsRef.current]);
      if (failedUploadsRef.current) {
        throw new Error("Some audio sections failed to upload. Recording could not be completed.");
      }
      const token = await session.getToken();
      const res = await axios.post(
        `${API_URL}/complete-recording`,
        { short_code: _short_code, session_id: metrics.sessionId },
        { headers: { "Content-Type": "application/json", TOKEN: token } },
      );
      console.log("Recording complete:", res.data);
      navigate(`/meetings/view/${_short_code}`);
    } catch (e) {
      console.error("Finalize failed:", e);
      window.alert(e.message || "Recording could not be completed. Please try again.");
    }
    setStitchingAudio(false);
    console.log("Recording metrics summary:", summarize());
    setRecording(false);
    setPaused(false);
    setStatus("Idle");
    try {
      stream?.getTracks?.().forEach((t) => t.stop());
      micStreamRef.current = null;
    } catch {}
  };

  const startRecording = async () => {
    try {
      const freshStream = await ensureMicPermission();
      await acquireWakeLock();
      startSession();
      if (!meetingStarted && !hasSetStartTimeRef.current) await setMeetingStartTime();
      if (isSafari()) {
        await startSafariWav(freshStream);
      } else {
        await startMediaRecorder(freshStream);
      }
    } catch (err) {
      console.error("Recording error:", err);
      alert("Recording failed: " + err.message);
      await releaseWakeLock();
      try {
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
      } catch {}
    }
  };

  async function setMeetingStartTime() {
    try {
      const token = await session.getToken();
   
      const requestedAt = Date.now();
      const _resp = await setMeetsStartTimeApi(meeting["_id"], token);
      if (_resp?.data?.Result === "SUCCESS") {
        setMeetingStarted(true);
        hasSetStartTimeRef.current = true;
        if (!meetingStartTsRef.current) {
          meetingStartTsRef.current = requestedAt;
        }
      }
    } catch (err) {
      console.error("setMeetingStartTime failed:", err);
    }
  }

  const pauseRecording = async () => {
    try {
      if (!recording || paused) return;
      if (isSafari()) {
        safariPausedRef.current = true;
      } else {
        mediaRecorderRef.current?.pause?.();
      }
      setPaused(true);
      stopTicking();
      setStatus("Paused");
    } catch (e) {
      console.error("Pause failed:", e);
    }
  };

  const resumeRecording = async () => {
    try {
      if (!recording || !paused) return;
      await acquireWakeLock();
      if (isSafari()) {
        stopSafariWavIfRunning();
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
        await startSafariWav(null);
      } else {
        if (mediaRecorderRef.current?.state === "paused") {
          mediaRecorderRef.current.resume();
        } else {
          mediaRecorderRef.current = null;
          micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
          micStreamRef.current = null;
          await startMediaRecorder(null);
        }
      }
      setPaused(false);
      startTicking();
      setStatus("Recording");
    } catch (e) {
      console.error("Resume failed, attempting full restart:", e);
      await startRecording();
    }
  };

  const finishRecording = async () => {
    try {
      if (!recording) return;
      if (isSafari()) {
        stopTicking();
        stopSafariWavIfRunning();
        const remaining = wavBufferRef.current;
        if (remaining && remaining.length > 2000) {
          const TARGET_SR = 16000;
          const rem16k = resampleLinear(remaining, wavSampleRateRef.current, TARGET_SR);
          const wavBlob = encodeWAV(rem16k, TARGET_SR);
          const idx = sectionIndexRef.current++;
          await uploadSection(new Blob([wavBlob], { type: "audio/wav" }), idx);
        }
        await releaseWakeLock();
        await finalizeSession(micStreamRef.current || new MediaStream());
      } else {
        if (mediaRecorderRef.current?.state === "paused") {
          try {
            mediaRecorderRef.current.resume();
          } catch {}
        }
        mediaRecorderRef.current?.stop();
        stopTicking();
        await releaseWakeLock();
      }
    } catch (e) {
      console.error("Finish failed:", e);
    }
  };

  useEffect(() => {
    if (recording && !meetingStarted && meeting && !hasSetStartTimeRef.current) {
      setMeetingStartTime();
    }
  }, [recording, meetingStarted, meeting]);

  const safeDescription = useMemo(() => {
    const text = meeting?.description || "";
    return text.length > 200 ? text.substring(0, 200) + "..." : text;
  }, [meeting]);

  const isLoading = meeting === null;

  const participantCount = meeting?.members?.length ?? 0;

  return (
    <div className="flex h-full flex-col relative">
      <div className="flex justify-end section-padding-x">
        <Button
          onPress={() => navigate("/")}
          size="sm"
          variant="ghost"
          isIconOnly
          className={"mt-4 glass-surface text-glass-foreground"}
        >
          <X size={10} className="text-[10px]" />
        </Button>
      </div>
      <div className="flex flex-col gap-2 text-accent-foreground/50 mt-4 section-padding-x">
        <div className="text-[45px] leading-[47px] font-bold">
          <div className="flex gap-3">
            <span className="text-accent-foreground">Record.</span>
            <span>Save.</span>
          </div>

          <span>Revisit anytime.</span>
        </div>

        <div className="font-medium flex gap-2 items-center text-accent-foreground font-normal text-sm tracking-[1px]">
          <Button
            className={"icon-button size-9"}
            isIconOnly
            onPress={() => setShowAddMember(true)}
            isDisabled={isUpdating}
          >
            <img src="/user-plus-white.svg" />
          </Button>

          {isUpdating || isLoading ? (
            <span className="inline-block h-3.5 w-36 rounded-full bg-default/30 dark:bg-white/20 animate-pulse" />
          ) : (
            <>
              <span className="font-medium">{participantCount} participants</span> in this Loopp
            </>
          )}
        </div>
      </div>

      <div>
        <img
          src="/active-meets-animation.gif"
          className={`mix-blend-lighten transition-opacity ${recording && !paused ? "opacity-100" : "opacity-40"}`}
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col items-center text-accent-foreground">
          {/* Meeting Title */}
          {isLoading ? (
            <SkeletonBar className="h-5 w-40" />
          ) : (
            <span className="font-bold text-lg capitalize">{meeting.title}</span>
          )}

          {isLoading ? (
            <SkeletonBar className="h-4 w-24 mt-1" />
          ) : (
            <span>Status: {status}</span>
          )}
        </div>

        <img src="/waveform.png" />
      </div>

      <div className="relative flex justify-center items-center section-padding-x text-accent-foreground">
        <img src="/active-focus-bar.png" className="border-glass border rounded-3xl" />
        {isLoading ? (
          <span className="absolute inline-block h-6 w-16 rounded-full bg-default/30 dark:bg-white/20 animate-pulse" />
        ) : (
          <span className="absolute text-xl font-bold tracking-[0.15px]">{formatTime(recordedTime)}</span>
        )}
        {/* <div className="border-accent-foreground/50 border rounded-3xl w-full h-[60px] flex items-center justify-center">
        </div> */}
      </div>

      <div className="section-padding-x mt-auto pb-20 grid grid-cols-2 gap-2">
        {!paused ? (
          <Button
            variant="secondary"
            className={"bg-glass text-glass-foreground-muted h-[53px] shadow-xl"}
            fullWidth
            onPress={pauseRecording}
            isDisabled={isLoading || !recording || stitchingAudio}
          >
            <Pause />
            Pause
          </Button>
        ) : (
          <Button
            variant="secondary"
            className={"bg-glass text-glass-foreground-muted h-[53px] shadow-xl"}
            fullWidth
            onPress={resumeRecording}
            isDisabled={isLoading || stitchingAudio}
          >
            <Play />
            Continue
          </Button>
        )}

        <Button
          variant="primary"
          fullWidth
          className={"text-accent bg-white font-bold h-[53px] rounded-full !py-[15px]"}
          onPress={finishRecording}
          isDisabled={isLoading || !recording || stitchingAudio}
        >
          {stitchingAudio ? "Finishing..." : "Finish"}
        </Button>
      </div>

      <AddMembersDrawer
        show={showAddMemeber}
        onResp={onAddMemberDrawerResp}
        initialParticipants={toConnectionShape(meeting?.members || [])}
        isConfirming={isUpdating}
      />
    </div>
  );
};

export default ActiveMeetsComp;